import SDiagBase from "./SDiagBase";

const ARCHETYPES = [
  {
    num: "01", color: "creator", name: "The Creator", tagline: "You build things.",
    body: "Creators are product and solution-oriented. They generate ideas constantly, prototype instinctively, and are most energised when building something that did not exist before. They ship early and iterate fast.",
    risk: "Dominant Creator founders need to watch for building ahead of the market and under-investing in distribution.",
    advantage: "Speed to product",
    risk_short: "Falling in love with the solution",
  },
  {
    num: "02", color: "connector", name: "The Connector", tagline: "You persuade people.",
    body: "Connectors are relationship and revenue-oriented. They open doors naturally, build trust quickly, and are most energised when in motion — meeting people, forming partnerships, generating early commercial momentum.",
    risk: "Dominant Connector founders need to watch for over-committing on relationship energy and under-investing in operational discipline.",
    advantage: "Early revenue signals",
    risk_short: "Diffuse focus",
  },
  {
    num: "03", color: "organiser", name: "The Organiser", tagline: "You create order.",
    body: "Organisers are process and execution-oriented. They build systems instinctively, hold teams accountable, and are most energised when complex moving parts run smoothly. They scale what others build.",
    risk: "Dominant Organiser founders need to watch for optimising too early and moving too slowly on decisions that require imperfect action.",
    advantage: "Operational reliability",
    risk_short: "Premature process over momentum",
  },
  {
    num: "04", color: "pathfinder", name: "The Pathfinder", tagline: "You see ahead.",
    body: "Pathfinders are vision and positioning-oriented. They think in markets, not features, and are most energised when connecting dots others have not yet connected — identifying where the opportunity is before it becomes obvious.",
    risk: "Dominant Pathfinder founders need to watch for staying in strategic thinking too long without testing assumptions.",
    advantage: "Clarity of direction",
    risk_short: "Paralysis by positioning",
  },
];

export default function S21DiagnosticPage() {
  return (
    <SDiagBase
      stage="S2.1"
      pageUrl="https://thepivotbox.com/S21diagnostic"
      pageTitle="Pivotbox S2.1 — Founder Archetype Assessment"
      testId="s21-diagnostic-page"
      hero={{
        eyebrowTrail: "Founder Archetype Assessment · S2.1",
        headlineLead: "The way you naturally build is",
        headlineItalic: "your real advantage.",
        sub: "Most career assessments measure what you are good at inside a system that already exists. S2.1 measures how you operate when there is no system — no job description, no team to inherit, no playbook to follow.",
        ctaLabel: "Unlock the full diagnostic",
        priceLabel: <>One-time fee &middot; <strong>&nbsp;&#8377;3,999</strong></>,
      }}
      intro={{
        label: "You have completed S1. Here is what comes next.",
        body: "S1 told you whether your life is positioned for the transition. S2.1 goes one level deeper. It tells you how you are wired to build — and where that wiring creates an advantage, and where it creates a blind spot, in year one.",
        bullets: [
          { strong: "Not a personality test.", body: "S2.1 does not describe who you are in general. It identifies your dominant operating mode specifically in a founder context — when there is no structure, no team, no clear playbook." },
          { strong: "Directly actionable.", body: "Your archetype determines what you should lead with in year one, where to find complementary support, and which business model types align with how you naturally operate." },
          { strong: "Feeds into S2.5.", body: "Your dominant and secondary archetype are two of the primary inputs into the timing and transition synthesis at the end of the diagnostic." },
        ],
        methodologyCols: [
          { label: "Methodology", body: "S2.1 is built on <strong>20 behavioural statements</strong> mapped to four founder archetypes, developed from research into how corporate professionals actually operate when they remove organisational structure from the equation." },
          { label: "What it measures", body: "Not your skills or your experience. Your <strong>natural instincts</strong> under founder conditions — how you generate ideas, build relationships, create order, and think about direction when you are operating without a support system." },
          { label: "How it is scored", body: "Each statement is rated 1\u20135. Your dominant archetype is your highest-averaging cluster. Where two clusters are within 0.5 points, <strong>both are identified</strong> as co-dominant — a common and meaningful pattern." },
        ],
      }}
      why={{
        eyebrow: "Why archetype matters",
        headlineLead: "Your archetype does not determine what you build.",
        headlineItalic: "It determines how.",
        paragraphs: [
          "Most career assessments measure capability within an existing structure. They tell you what role you should play in a company someone else built. S2.1 asks a different question: when there is no structure, no inherited team, and no clear playbook — how do you naturally operate?",
          "Knowing your archetype does not tell you what kind of business to build. It tells you how you will approach the earliest decisions — what you will prioritise instinctively, where you will generate early momentum, and where you will need to either develop deliberately or find complementary support.",
          "The highest-risk transition is the one where a founder's <strong>natural archetype conflicts with what the business model demands in year one.</strong> A Pathfinder building a business that requires high-velocity relationship selling will face a structural mismatch. S2.1 surfaces this before the move, not during it.",
        ],
        callout: "The way you naturally operate is not something to work around. It is the architecture of your early competitive advantage — if you build with it, not against it.",
      }}
      measures={{
        eyebrow: "What S2.1 maps",
        headline: "Four founder archetypes. One dominant mode.",
        intro: "Most people have elements of all four. S2.1 identifies which one or two are clearly dominant — the mode through which you will instinctively approach every early decision, challenge, and opportunity.",
        cards: (
          <div className="sd-arch-grid">
            {ARCHETYPES.map((a) => (
              <div className="sd-arch-card" key={a.num} data-color={a.color}>
                <span className="sd-arch-num">{a.num}</span>
                <h3 className="sd-arch-name">{a.name}</h3>
                <p className="sd-arch-tagline">{a.tagline}</p>
                <p className="sd-arch-body">{a.body}</p>
                <p className="sd-arch-driver" style={{ marginBottom: "0.6rem" }}>{a.risk}</p>
                <div className="sd-arch-lists">
                  <div>
                    <div className="sd-arch-list-label">Year-1 advantage</div>
                    <p style={{ fontSize: "0.86rem", color: "#1F5D3F", margin: 0, fontWeight: 600 }}>{a.advantage}</p>
                  </div>
                  <div>
                    <div className="sd-arch-list-label">Year-1 risk</div>
                    <p style={{ fontSize: "0.86rem", color: "#9A3E1F", margin: 0, fontWeight: 600 }}>{a.risk_short}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ),
      }}
      receive={{
        eyebrow: "Part of the full Pivotbox diagnostic",
        eyebrowAccent: "orange",
        callout: "S2.1 sits inside the same consolidated Pivotbox report as S1, S2.2 and S2.3 \u2014 your archetype is one of <strong>4 integrated assessments</strong> that together form your complete diagnostic.",
        heading: "A precise mirror to how you naturally build.",
        intro: "Your S2.1 archetype profile is shown as a score across the four founder modes. Each bar reflects how strongly that mode shows up in you — not what you are good at in a job, but how you instinctively operate when there is no structure to lean on.",
        bullets: [],
        report: (
          <div className="s1d-report sd-report-archetype">
            <div className="s1d-report__head">
              <span className="s1d-report__title">Pivotbox Diagnostic Report</span>
              <span className="s1d-report__badge">S2.1 COMPLETE</span>
            </div>
            <div className="s1d-report__band">
              <span className="s1d-report__band-label">ARCHETYPE PROFILE</span>
              <span className="s1d-report__band-value">Out of 5</span>
            </div>
            <div className="s1d-report__bars">
              {[
                { label: "Creator",    score: 4.3, color: "#B7397A" },
                { label: "Pathfinder", score: 3.9, color: "#0E7C5C" },
                { label: "Connector",  score: 3.5, color: "#B45309" },
                { label: "Organiser",  score: 3.1, color: "#2557C7" },
              ].map((d) => (
                <div className="s1d-bar2" key={d.label}>
                  <div className="s1d-bar2__head">
                    <span className="s1d-bar2__label">{d.label}</span>
                    <span className="s1d-bar2__value" style={{ color: d.color }}>{d.score.toFixed(1)}<span style={{ color: "#7A7268", fontWeight: 500, marginLeft: 2 }}>/5</span></span>
                  </div>
                  <div className="s1d-bar2__row">
                    <div className="s1d-bar2__track"><div className="s1d-bar2__fill" style={{ width: `${(d.score / 5) * 100}%`, background: d.color }} /></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ),
      }}
      helps={{
        eyebrow: "How S2.1 helps",
        heading: "Three things S2.1 makes clear.",
        intro: "S2.1 is not a personality type label. It is a strategic input into how you should structure your early transition — what to lead with, what to guard against, and where to build support.",
        cards: [
          { num: "01", title: "Your natural first-year operating mode", body: "S2.1 identifies whether you will instinctively lead with building, connecting, organising, or positioning — and what that means for how your first 12 months should be structured." },
          { num: "02", title: "Where your archetype creates risk", body: "Every archetype has a predictable blind spot in a founder transition. S2.1 surfaces yours specifically so you can manage it before it becomes a pattern." },
          { num: "03", title: "Where to find complementary support", body: "The gaps created by a dominant archetype are areas to cover through deliberate hiring, partnership, or advisory relationships. S2.1 tells you which gaps matter most." },
        ],
      }}
      framework={[
        { stage: "S0",   name: "Founder Signal Test",                     q: "Are you wired to build? Scenario-based signals across founder instincts.", badge: "Completed", isFree: true, isCompleted: true },
        { stage: "S1",   name: "Founder Reality Check",                   q: "Is your life positioned to support this transition?", badge: "Completed", isCompleted: true },
        { stage: "S2",   name: "Founder Diagnostic",                      q: "Who are you as a founder — how you build, hold up, and execute." },
        { stage: "S2.1", name: "Founder Archetype Assessment",            q: "How are you naturally wired to build?", badge: "You are here", isCurrent: true, isSub: true },
        { stage: "S2.2", name: "Founder Durability Assessment",           q: "How do you respond to failure, setback, and sustained uncertainty?", isSub: true, isFuture: true },
        { stage: "S2.3", name: "Founder Capability & Execution Profile",  q: "Do you have the capabilities to execute your intended business model?", isSub: true, isFuture: true },
        { stage: "S3",   name: "Strategy Blueprint",                      q: "Is the venture concept, model, and market validated enough to commit?" },
        { stage: "S4",   name: "Founder Operating System",                q: "How do you go from first revenue to a business that scales?" },
      ]}
      finalCta={{
        heading: "Know your natural mode before the pressure starts.",
        sub: "Twenty focused statements. A dominant archetype profile, a second mode, and a precise map of the gaps your wiring creates in a founder transition.",
        price: { strike: "7,999", current: "3,999" },
        ctaLabel: "Unlock the full diagnostic",
      }}
    />
  );
}
